import type { Library, Notebook } from './library'
import type { QirtaasDocumentSummary } from './qirtaas'

// Arabic Lab "My course": practice drawn from the user's own lesson notes. The notes are only read
// (server side, on preview); this module holds the client API plus pure helpers shared with tests.
const API='https://api.dawwin.al-ilm.net/arabic-course'
export type CourseItemKind='sentence'|'word'|'phrase'|'root'
export type CourseItemDraft={kind:CourseItemKind;arabic:string;transliteration:string;english:string;note:string;label:string;root:string;plural_arabic:string;plural_transliteration:string;plural_english:string}
export type CourseItem=CourseItemDraft&{id:string;document_id:string;position:number}
export type ImportedLesson={document_id:string;notebook_id:string;title:string;lesson_number:number|null;item_count:number;source_updated_at:string;imported_at:string}
export type CourseRoot={notebook_id:string;audio_collection:string;added_at:string}
export type Course={root_notebook_id:string;lessons:ImportedLesson[];items:CourseItem[];courses?:CourseRoot[]}
export type ItemStatus='new'|'saved'|'dismissed'
export type LessonPreview={document_id:string;title:string;source_updated_at:string;items:(CourseItemDraft&{status?:ItemStatus})[]}
export type CourseMark={verdict:'correct'|'partial'|'incorrect';feedback:string;marked_by:'rule'|'haiku'}

async function call<T>(path='',init:RequestInit={}){const r=await fetch(API+path,{...init,credentials:'include',headers:{Accept:'application/json','Content-Type':'application/json',...(init.headers||{})}});if(!r.ok)throw new Error((await r.json().catch(()=>null))?.error||('http_'+r.status));return r.status===204?null as T:await r.json() as T}
export const getCourse=()=>call<Course>()
export const setCourseRoot=(root_notebook_id:string)=>call<{root_notebook_id:string}>('/settings',{method:'PUT',body:JSON.stringify({root_notebook_id})})
export const previewLesson=(document_id:string)=>call<LessonPreview>('/preview',{method:'POST',body:JSON.stringify({document_id})})
export const saveLesson=(document_id:string,body:{notebook_id:string;title:string;lesson_number:number|null;source_updated_at:string;items:CourseItemDraft[];dismissed?:CourseItemDraft[]})=>call<{lesson:ImportedLesson}>('/lessons/'+encodeURIComponent(document_id),{method:'PUT',body:JSON.stringify(body)})
export const removeLesson=(document_id:string)=>call<null>('/lessons/'+encodeURIComponent(document_id),{method:'DELETE'})
export const removeCourseItem=(id:string)=>call<null>('/items/'+encodeURIComponent(id),{method:'DELETE'})
export const markCourseAnswer=(item_id:string,answer:string)=>call<CourseMark>('/mark',{method:'POST',body:JSON.stringify({item_id,answer})})
export const setCourseAudio=(notebook_id:string,audio_collection:string)=>call<{course:CourseRoot}>('/courses/'+encodeURIComponent(notebook_id),{method:'PUT',body:JSON.stringify({audio_collection})})
export const removeCourseRoot=(notebook_id:string)=>call<null>('/courses/'+encodeURIComponent(notebook_id),{method:'DELETE'})

// ── Course audio: the learner's own copy, private to their account ──────────────────────────────────
export type CourseAudio={id:string;collection:string;unit:number;track:number;title:string;original_name:string;mime_type:string;byte_size:number;created_at:string}
export const listCourseAudio=()=>call<{audio:CourseAudio[]}>('/audio')
export const audioUrl=(id:string)=>`${API}/audio/${encodeURIComponent(id)}/content`
export const deleteCourseAudio=(id:string)=>call<null>('/audio/'+encodeURIComponent(id),{method:'DELETE'})
/** One track per request, sent as the raw file; the server files it by the unit and track in its name. */
export async function uploadCourseAudio(file:File,collection:string){
 const q=new URLSearchParams({collection,filename:file.name})
 const r=await fetch(`${API}/audio?${q}`,{method:'POST',credentials:'include',headers:{Accept:'application/json','Content-Type':/\.m4a$/i.test(file.name)?'audio/mp4':'audio/mpeg'},body:file})
 if(!r.ok)throw new Error((await r.json().catch(()=>null))?.error||('http_'+r.status))
 return await r.json() as {audio:CourseAudio}
}
export const unitLabel=(unit:number)=>unit===0?'Introduction':`Unit ${unit}`
/** "Alimiyyah › Foundation › Arabic Language › Mastering Arabic 1", for choosing a course notebook at any depth. */
export function notebookPath(notebooks:Notebook[],id:string){
 const byId=new Map(notebooks.map(n=>[n.id,n])),names:string[]=[]
 for(let n=byId.get(id),guard=0;n&&guard<20;n=n.parent_id?byId.get(n.parent_id):undefined,guard++)names.unshift(n.name)
 return names.join(' › ')
}

// ── The course map ──────────────────────────────────────────────────────────────────────────────────
/**
 * "96. Language Lesson 26" → 96, "59B. Bonus" → 59.2, "93A. Summary" → 93.1; titles that follow a course's own scheme,
 * "Lecture 03", "Session 1: …", "Lesson 6 – …", "Module 01", "Unit 4", "Chapter 2", "Class 7", "Week 3" (with or without
 * a leading zero, "Lesson 5B" → 5.2), are numbered too. null when unnumbered.
 */
export function lessonNumber(title:string):number|null{
 const named=title.trim().match(/^(?:session|lesson|module|lecture|unit|chapter|class|week)\s*(\d{1,4})([A-Za-z])?(?=$|[\s.:,;)\]–—-])/i)
 const m=named||title.trim().match(/^(\d{1,4})([A-Za-z])?[.)\s]/)
 if(!m)return null
 return Number(m[1])+(m[2]?(m[2].toUpperCase().charCodeAt(0)-64)/10:0)
}
export type CourseLesson={document_id:string;notebook_id:string;section:string;title:string;lesson_number:number|null;updated_at:string}
export function descendants(notebooks:Notebook[],rootId:string):Set<string>{
 const ids=new Set([rootId]);let grew=true
 while(grew){grew=false;for(const n of notebooks)if(n.parent_id&&ids.has(n.parent_id)&&!ids.has(n.id)){ids.add(n.id);grew=true}}
 return ids
}
/** Every titled note inside the course notebook, in lesson order, with the top-level section it sits in. */
export function courseLessons(library:Pick<Library,'notebooks'|'documents'>,documents:QirtaasDocumentSummary[],rootId:string):CourseLesson[]{
 if(!rootId)return[]
 const inCourse=descendants(library.notebooks,rootId),byId=new Map(library.notebooks.map(n=>[n.id,n]))
 const section=(id:string)=>{let n=byId.get(id);while(n&&n.parent_id&&n.parent_id!==rootId)n=byId.get(n.parent_id);return n&&n.id!==rootId?n.name:''}
 const titles=new Map(documents.map(d=>[d.id,d]))
 return library.documents
  .filter(d=>d.notebook_id&&inCourse.has(d.notebook_id)&&titles.get(d.qirtaas_document_id)?.title?.trim())
  .map(d=>{const doc=titles.get(d.qirtaas_document_id)!;return{document_id:d.qirtaas_document_id,notebook_id:d.notebook_id!,section:section(d.notebook_id!),title:doc.title.trim(),lesson_number:lessonNumber(doc.title),updated_at:doc.updated_at||''}})
  .sort((a,b)=>(a.lesson_number??1e9)-(b.lesson_number??1e9)||a.title.localeCompare(b.title,undefined,{numeric:true}))
}
/** Lessons never imported, or edited since they were last imported. */
export function lessonsToImport(lessons:CourseLesson[],imported:ImportedLesson[]){
 const done=new Map(imported.map(l=>[l.document_id,l]))
 return lessons.filter(l=>{const i=done.get(l.document_id);return!i||(!!l.updated_at&&!!i.source_updated_at&&l.updated_at>i.source_updated_at)})
}
/** Suggests the course notebook: a top-level notebook whose name mentions Arabic and holds the most notes. */
export function suggestCourseRoot(library:Pick<Library,'notebooks'|'documents'>):string{
 const tops=library.notebooks.filter(n=>!n.parent_id&&/arab/i.test(n.name))
 let best='',most=0
 for(const n of tops){const ids=descendants(library.notebooks,n.id),count=library.documents.filter(d=>d.notebook_id&&ids.has(d.notebook_id)).length;if(count>most){most=count;best=n.id}}
 return best
}

// ── Practice ────────────────────────────────────────────────────────────────────────────────────────
export type PracticeMode='listen'|'read'|'build'|'write'|'shadow'
export type PracticeScope='recent'|'all'|string // a section name
/** Glosses sometimes keep the transliteration in front ("*Maktabun* – a desk"); show only the meaning. */
export const cleanEnglish=(english:string)=>english.replace(/^\*[^*]+\*\s*[–-]\s*/,'').trim()
export const arabicWords=(arabic:string)=>arabic.replace(/[؟?!.،,]/g,' ').trim().split(/\s+/).filter(Boolean)
export function seeded(seed:number){let s=seed>>>0||1;return()=>{s=(s*1664525+1013904223)>>>0;return s/2**32}}
export function shuffled<T>(list:T[],random:()=>number=Math.random):T[]{const a=[...list];for(let i=a.length-1;i>0;i--){const j=Math.floor(random()*(i+1));[a[i],a[j]]=[a[j],a[i]]}return a}
/** Word tiles for Build; reshuffles until the order differs from the answer whenever that is possible. */
export function buildTiles(arabic:string,random:()=>number=Math.random){
 const words=arabicWords(arabic);if(words.length<2)return words
 for(let i=0;i<8;i++){const t=shuffled(words,random);if(t.join(' ')!==words.join(' '))return t}
 return[...words].reverse()
}
export const isBuildCorrect=(arabic:string,chosen:string[])=>arabicWords(arabic).join(' ')===chosen.join(' ')
/** Same identity as the server's "already saved" check: the Arabic and its meaning. */
export const itemKey=(arabic:string,english:string)=>`${arabic.trim().replace(/[؟?!.،,]+$/,'').trim()}|${english.trim().toLowerCase().replace(/[.!]+$/,'')}`
export function itemsForMode(items:CourseItem[],mode:PracticeMode){
 const seen=new Set<string>()
 // The same word met in several lessons is practised once (first lesson wins).
 const usable=items.filter(i=>{if(i.kind==='root'||!i.english)return false;const k=itemKey(i.arabic,i.english);if(seen.has(k))return false;seen.add(k);return true})
 if(mode==='shadow')return usable.filter(i=>(i.kind==='phrase'||i.kind==='sentence')&&arabicWords(i.arabic).length<=10)
 if(mode==='build')return usable.filter(i=>{const n=arabicWords(i.arabic).length;return n>=2&&n<=8})
 return usable
}
/** Scope: the most recent ten lessons, everything, or one section. Order follows the lessons. */
export function scopedItems(items:CourseItem[],lessons:CourseLesson[],scope:PracticeScope){
 const order=new Map(lessons.map((l,i)=>[l.document_id,i]))
 const known=items.filter(i=>order.has(i.document_id)).sort((a,b)=>order.get(a.document_id)!-order.get(b.document_id)!||a.position-b.position)
 if(scope==='all')return known
 if(scope==='recent'){const ids=new Set(lessons.filter(l=>known.some(i=>i.document_id===l.document_id)).slice(-10).map(l=>l.document_id));return known.filter(i=>ids.has(i.document_id))}
 const ids=new Set(lessons.filter(l=>l.section===scope).map(l=>l.document_id))
 return known.filter(i=>ids.has(i.document_id))
}

/** Per-item practice history, kept in this browser. */
export type ItemHistory=Record<string,{seen:number;right:number;last:string}>
const HISTORY='dawwin-arabic-course-history-v1'
export function readHistory():ItemHistory{try{const v=JSON.parse(localStorage.getItem(HISTORY)||'{}');return v&&typeof v==='object'&&!Array.isArray(v)?v:{}}catch{return{}}}
export function recordResult(history:ItemHistory,id:string,right:boolean,now=new Date().toISOString()):ItemHistory{
 const h=history[id]||{seen:0,right:0,last:''}
 const next={...history,[id]:{seen:h.seen+1,right:h.right+(right?1:0),last:now}}
 try{localStorage.setItem(HISTORY,JSON.stringify(next))}catch{/* storage may be unavailable */}
 return next
}
/** Unseen items first (in lesson order), then the ones missed most, then the least recently practised. */
export function practiceOrder(items:CourseItem[],history:ItemHistory){
 return items.map((item,index)=>({item,index})).sort((a,b)=>{
  const ha=history[a.item.id],hb=history[b.item.id]
  if(!ha||!hb)return ha?1:hb?-1:a.index-b.index
  return(hb.seen-hb.right)-(ha.seen-ha.right)||ha.last.localeCompare(hb.last)
 }).map(x=>x.item)
}

/** Transliteration support fades once the reading lessons are behind you; the learner can always override. */
export type TranslitMode='show'|'tap'|'hide'
const TRANSLIT='dawwin-arabic-course-translit'
export function defaultTranslit(lessons:CourseLesson[]):TranslitMode{
 const highest=Math.max(0,...lessons.map(l=>l.lesson_number??0))
 return highest>=24?'tap':'show'
}
export function readTranslit():TranslitMode|null{try{const v=localStorage.getItem(TRANSLIT);return v==='show'||v==='tap'||v==='hide'?v:null}catch{return null}}
export function writeTranslit(v:TranslitMode){try{localStorage.setItem(TRANSLIT,v)}catch{/* storage may be unavailable */}}

// ── Daily Five questions from the course ────────────────────────────────────────────────────────────
export type DailyPrompt={kind:string;question:string;arabic:string;options:string[];answer:string;explain:string}
/**
 * Multiple-choice checks built from imported items, weighted to the latest lessons. Distractors come from the
 * same kind of item so the options are plausible; questions without three distinct options are skipped.
 */
export function coursePrompts(items:CourseItem[],lessons:CourseLesson[]):DailyPrompt[]{
 const ordered=scopedItems(items,lessons,'all'),recent=new Set(scopedItems(items,lessons,'recent').map(i=>i.id))
 const prompts:DailyPrompt[]=[]
 const pick=(pool:string[],answer:string,n=2)=>{const out:string[]=[];for(const v of pool)if(v&&v!==answer&&!out.includes(v)){out.push(v);if(out.length===n)break}return out}
 const words=ordered.filter(i=>i.kind==='word'&&i.english),sentences=ordered.filter(i=>(i.kind==='sentence'||i.kind==='phrase')&&i.english)
 const near=(list:CourseItem[],i:CourseItem)=>{const k=list.indexOf(i);return[...list.slice(k+1,k+12),...list.slice(Math.max(0,k-12),k)]}
 for(const i of [...words,...sentences]){
  const pool=near(i.kind==='word'?words:sentences,i)
  const wrong=pick(pool.map(x=>x.english.toLowerCase()),i.english.toLowerCase())
  if(wrong.length===2)prompts.push({kind:recent.has(i.id)?'RECENT':'MEANING',question:'What does this mean?',arabic:i.arabic,options:shuffled([i.english.toLowerCase(),...wrong],seeded(i.arabic.length*31+i.position)),answer:i.english.toLowerCase(),explain:`${i.transliteration?i.transliteration+' – ':''}${i.english}${i.note?' ('+i.note+')':''}`})
  if(i.transliteration&&i.kind==='word'){
   const wrongT=pick(pool.map(x=>x.transliteration),i.transliteration)
   if(wrongT.length===2)prompts.push({kind:'READING',question:'Which transliteration matches?',arabic:i.arabic,options:shuffled([i.transliteration,...wrongT],seeded(i.position*17+3)),answer:i.transliteration,explain:`${i.arabic} is read ${i.transliteration}: ${i.english}.`})
  }
  if(i.plural_arabic){
   const wrongP=pick(words.map(x=>x.plural_arabic||x.arabic),i.plural_arabic)
   if(wrongP.length===2)prompts.push({kind:'PLURAL',question:'Which is the plural?',arabic:i.arabic,options:shuffled([i.plural_arabic,...wrongP],seeded(i.position*7+1)),answer:i.plural_arabic,explain:`${i.arabic} (${i.english}) → ${i.plural_arabic}${i.plural_english?' ('+i.plural_english+')':''}.`})
  }
  if(i.root){
   const roots=[...new Set(ordered.filter(x=>x.kind==='root').map(x=>x.arabic))]
   const wrongR=pick(roots,i.root)
   if(wrongR.length===2)prompts.push({kind:'ROOTS',question:'Which root family does this word belong to?',arabic:i.arabic,options:shuffled([i.root,...wrongR],seeded(i.position*13+5)),answer:i.root,explain:`${i.arabic} (${i.english}) comes from the root ${i.root}.`})
  }
 }
 // Recent lessons first so the daily rotation leans towards what is being studied now.
 return[...prompts.filter(p=>p.kind==='RECENT'),...prompts.filter(p=>p.kind!=='RECENT')]
}

// ── Phase 2: verb forms, checkpoints, roots ─────────────────────────────────────────────────────────
const PERSON=String.raw`(?:I|we|he|she|it|they(?: two| both| all| women| men)?|you(?: two| both| all| women| men)?|the two [a-z]+)`
const VERB_FORM=new RegExp(String.raw`^(${PERSON})\s+([a-z]+)\s*[.!]?\s*(?:(?:,|\(|–|-)\s*(.*?))?\)?$`,'i')
export type VerbGroup={verb:string;forms:CourseItem[]}
/**
 * Single-word items glossed "<person> <verb>" ("She wrote", "You women wrote", "You wrote, addressing one
 * woman") grouped by the English verb, so near-identical Arabic forms can be told apart. A gloss shared by two
 * different Arabic forms ("You all wrote" for both كَتَبْتُمْ and كَتَبْتُنَّ) is ambiguous and left out.
 */
export function verbGroups(items:CourseItem[]):VerbGroup[]{
 const groups=new Map<string,CourseItem[]>()
 for(const i of items){
  if(i.kind!=='word'||arabicWords(i.arabic).length!==1)continue
  const m=i.english.trim().match(VERB_FORM)
  if(!m||/^(was|were|is|are|am|be)$/i.test(m[2]))continue
  const verb=m[2].toLowerCase(),list=groups.get(verb)||[]
  if(!list.some(x=>x.arabic===i.arabic))list.push(i)
  groups.set(verb,list)
 }
 const out:VerbGroup[]=[]
 for(const [verb,forms] of groups){
  const byGloss=new Map<string,Set<string>>()
  for(const f of forms){const g=f.english.trim().toLowerCase().replace(/[.!]+$/,'');byGloss.set(g,(byGloss.get(g)||new Set()).add(f.arabic))}
  const clear=forms.filter(f=>byGloss.get(f.english.trim().toLowerCase().replace(/[.!]+$/,''))!.size===1)
  if(clear.length>=3)out.push({verb,forms:clear})
 }
 return out.sort((a,b)=>b.forms.length-a.forms.length||a.verb.localeCompare(b.verb))
}
export type ChoiceQuestion={item:CourseItem;prompt:string;promptArabic:boolean;options:string[];answer:string;optionsArabic:boolean}
/** One verb-form question: Arabic → which person, or English → which Arabic form. Distractors come from the same verb. */
export function verbQuestion(group:VerbGroup,random:()=>number=Math.random):ChoiceQuestion{
 return verbQuestionFor(group,group.forms[Math.floor(random()*group.forms.length)],random)
}
export function verbQuestionFor(group:VerbGroup,item:CourseItem,random:()=>number=Math.random):ChoiceQuestion{
 const others=shuffled(group.forms.filter(f=>f!==item),random).slice(0,3)
 const toArabic=random()<.5
 return toArabic
  ?{item,prompt:item.english,promptArabic:false,options:shuffled([item,...others].map(f=>f.arabic),random),answer:item.arabic,optionsArabic:true}
  :{item,prompt:item.arabic,promptArabic:true,options:shuffled([item,...others].map(f=>f.english),random),answer:item.english,optionsArabic:false}
}
/**
 * A section checkpoint: up to `count` questions spread across the section's lessons — mostly Arabic → meaning,
 * some meaning → Arabic, and verb forms when the section has them. Distractors come from the same section.
 */
export function checkpointQuestions(items:CourseItem[],lessons:CourseLesson[],section:string,random:()=>number=Math.random,count=20):ChoiceQuestion[]{
 const pool=itemsForMode(scopedItems(items,lessons,section),'read')
 const byLesson=new Map<string,CourseItem[]>()
 for(const i of pool)byLesson.set(i.document_id,[...(byLesson.get(i.document_id)||[]),i])
 // Round-robin across lessons so every lesson is represented before any repeats.
 const queues=[...byLesson.values()].map(l=>shuffled(l,random)),picked:CourseItem[]=[]
 while(picked.length<count&&queues.some(q=>q.length))for(const q of queues){const i=q.shift();if(i&&picked.length<count)picked.push(i)}
 const questions:ChoiceQuestion[]=[]
 const verbs=verbGroups(pool)
 picked.forEach((item,n)=>{
  const group=verbs.find(g=>g.forms.includes(item))
  if(group&&n%3===0){questions.push(verbQuestionFor(group,item,random));return}
  const same=pool.filter(x=>x!==item&&x.kind===item.kind&&x.english.toLowerCase()!==item.english.toLowerCase()&&x.arabic!==item.arabic)
  const others=shuffled(same,random).filter((x,k,a)=>a.findIndex(y=>y.english.toLowerCase()===x.english.toLowerCase())===k).slice(0,3)
  if(others.length<2)return
  if(n%4===3)questions.push({item,prompt:item.english,promptArabic:false,options:shuffled([item,...others].map(x=>x.arabic),random),answer:item.arabic,optionsArabic:true})
  else questions.push({item,prompt:item.arabic,promptArabic:true,options:shuffled([item,...others].map(x=>x.english),random),answer:item.english,optionsArabic:false})
 })
 return questions
}
export type CheckpointRecord={best:number;last:number;total:number;date:string}
const CHECKPOINTS='dawwin-arabic-course-checkpoints-v1'
export const PASS_MARK=.8
export function readCheckpoints():Record<string,CheckpointRecord>{try{const v=JSON.parse(localStorage.getItem(CHECKPOINTS)||'{}');return v&&typeof v==='object'&&!Array.isArray(v)?v:{}}catch{return{}}}
export function recordCheckpoint(section:string,score:number,total:number,now=new Date().toISOString()){
 const all=readCheckpoints(),prev=all[section]
 const next={...all,[section]:{best:Math.max(prev?.best??0,score/total),last:score/total,total,date:now}}
 try{localStorage.setItem(CHECKPOINTS,JSON.stringify(next))}catch{/* storage may be unavailable */}
 return next
}
// Letters only, hamza seats unified and the article removed, so root letters can be looked for in a word.
export function skeleton(arabic:string){
 return arabic.replace(/[ً-ْٰـ]/g,'').replace(/[أإآٱؤئ]/g,'ء').replace(/ى/g,'ي').replace(/[^ء-ي]/g,'').replace(/^ال(?=..)/,'')
}
/**
 * True when the root letters occur in the word in order with at most one letter between them (كاتب, مكتبة for
 * ك ت ب). Roots with a weak letter (و ي ا ء) are never matched this way: long vowels make the guess unreliable.
 */
export function hasRootLetters(arabic:string,root:string){
 const word=skeleton(arabic),letters=skeleton(root).split('')
 if(letters.length<3||letters.some(l=>'ويائ'.includes(l)||l==='ء'))return false
 for(let start=0;start<word.length;start++){
  if(word[start]!==letters[0])continue
  let at=start,ok=true
  for(const l of letters.slice(1)){const next=word.indexOf(l,at+1);if(next<0||next-at>2){ok=false;break}at=next}
  if(ok)return true
 }
 return false
}
export type RootFamily={root:CourseItem;listed:CourseItem[];matched:CourseItem[]}
/**
 * Roots found in the notes, with the words the notes list under them ("listed") and other single words from the
 * course that contain the root letters in order ("matched" – a reading aid, shown as such). Course order.
 */
export function rootFamilies(items:CourseItem[],lessons:CourseLesson[]):RootFamily[]{
 const ordered=scopedItems(items,lessons,'all'),families=new Map<string,RootFamily>()
 for(const r of ordered)if(r.kind==='root'&&!families.has(r.arabic))families.set(r.arabic,{root:r,listed:[],matched:[]})
 const words=ordered.filter(i=>i.kind==='word'&&i.english)
 for(const f of families.values()){
  const seen=new Set<string>()
  for(const i of ordered)if(i.root===f.root.arabic&&!seen.has(skeleton(i.arabic))){seen.add(skeleton(i.arabic));f.listed.push(i)}
  for(const w of words)if(!seen.has(skeleton(w.arabic))&&hasRootLetters(w.arabic,f.root.arabic)){seen.add(skeleton(w.arabic));f.matched.push(w)}
 }
 return[...families.values()]
}
